/**
 * CommandPalette.tsx — die globale Kommando-Palette (⌘K), docs/30 §9.2.
 *
 * EIN natives <dialog>, per showModal() modal geoeffnet. Cmd/Strg+K oeffnet sie
 * ueber die Kommando-Registry (useCommandScope). Die Palette durchsucht mit
 * einem leichtgewichtigen Subsequence-Fuzzy-Score:
 *   (a) alle aktuell aktiven Kommandos (getRegisteredCommands, gegated auf when)
 *   (b) alle Entitaeten aus dem LOKALEN Dexie/React-Query-Cache — Charaktere,
 *       Weltelemente, Kampagnen, Tischrunden — ueber die vorhandenen ../sync-
 *       Hooks. KEIN neuer Endpoint, KEIN fetch: die Hooks lesen local-first.
 *
 * Jede Aktion ist damit AUCH ohne Kuerzel erreichbar (die Palette listet sie
 * auf, Enter/Klick aktiviert). Kommando-Treffer rufen cmd.run(ctx); Entitaets-
 * Treffer navigieren auf die echte Route (Charakter -> /character-chat/:id,
 * Weltelement -> /world-element-edit?id=:id [Alt-Vertrag H], Kampagne ->
 * /chat/:id, Tischrunde -> /table-round-chat/:id).
 *
 * Overlay-Semantik: beim Oeffnen wird der Eintrag auf den zentralen overlayStack
 * gelegt, damit Esc/Hardware-Back GENAU diese Ebene schliessen (peekTop). Es gibt
 * KEINEN eigenen window-keydown-Listener — Esc laeuft ueber das native <dialog>
 * (onCancel), gegated auf peekTop().
 */

import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation, useNavigate, type NavigateFunction } from 'react-router-dom';
import { useT } from '../i18n';
import { useSizeClass } from '../layout/sizeClass';
import { Icon } from '../components/ui/Icon';
import {
  useCampaignsLocal,
  useCharactersLocal,
  useTableRoundsLocal,
  useWorldElementsLocal,
} from '../sync';
import type { CommandContext } from './useHotkeys';
import { getRegisteredCommands, useCommandScope, type Command } from './registry';
import { getOverlayDepth, peekTop, pushOverlay } from './overlayStack';
import './command-palette.css';

// ---------------------------------------------------------------------------
// Fuzzy-Suche — leichtgewichtiges Subsequence-Scoring (keine neue Dependency)
// ---------------------------------------------------------------------------

/**
 * Subsequence-Fuzzy-Score, case-insensitive. Gibt eine Punktzahl (hoeher =
 * besser) oder null zurueck, wenn `query` keine Teilfolge von `text` ist.
 *
 * Bewertung: aufeinanderfolgende Treffer (streak) und Treffer am Wortanfang
 * (nach Leerzeichen/Nicht-Buchstaben oder am Textanfang) zaehlen mehr — so
 * ranken „echte" Praefix-/Wortgrenzen-Treffer vor verstreuten Buchstaben.
 * Bewusst O(text.length): eine lineare Durchsicht, kein Backtracking — deshalb
 * bleibt die Filterung auch bei >1000 Entitaeten deutlich unter dem
 * Performance-Gate (docs/30 §5.5, gemessen im Test).
 */
export function fuzzyScore(text: string, query: string): number | null {
  if (!query) return 0;
  const hay = text.toLowerCase();
  const needle = query.toLowerCase();

  let score = 0;
  let streak = 0;
  let qi = 0;
  let prevWasBoundary = true;

  for (let hi = 0; hi < hay.length && qi < needle.length; hi += 1) {
    const hc = hay[hi];
    if (hc === needle[qi]) {
      score += 1;
      streak += 1;
      score += streak; // aufeinanderfolgende Treffer belohnen
      if (prevWasBoundary) score += 4; // Wortanfang belohnen
      qi += 1;
    } else {
      streak = 0;
    }
    prevWasBoundary = hc === ' ' || hc === '-' || hc === '_' || !/[a-z0-9]/.test(hc);
  }

  if (qi < needle.length) return null; // nicht alle Buchstaben gefunden
  // Kuerzere Treffer leicht bevorzugen (dichtere Uebereinstimmung).
  score -= Math.max(0, hay.length - needle.length) * 0.01;
  return score;
}

// ---------------------------------------------------------------------------
// Ergebnis-Typen
// ---------------------------------------------------------------------------

type ResultKind = 'command' | 'character' | 'world' | 'campaign' | 'round';

interface PaletteResult {
  /** Eindeutige option-id (fuer aria-activedescendant + Auswahl). */
  key: string;
  kind: ResultKind;
  label: string;
  score: number;
  /** Icon-Name (Sprite, ohne icon-Prefix). */
  icon: string;
  /** Aktion beim Aktivieren. */
  run: () => void;
}

const SECTION_ORDER: ResultKind[] = ['command', 'character', 'world', 'campaign', 'round'];

const SECTION_TITLE_KEY: Record<ResultKind, string> = {
  command: 'command.section.commands',
  character: 'command.section.characters',
  world: 'command.section.world',
  campaign: 'command.section.campaigns',
  round: 'command.section.rounds',
};

const SECTION_ICON: Record<ResultKind, string> = {
  command: 'search',
  character: 'nav-characters',
  world: 'nav-worlds',
  campaign: 'nav-adventure',
  round: 'nav-gamebuild',
};

// ---------------------------------------------------------------------------
// Hilfen
// ---------------------------------------------------------------------------

function nameOf(entity: { name?: unknown }, fallback: string): string {
  const raw = entity.name;
  const trimmed = typeof raw === 'string' ? raw.trim() : '';
  return trimmed.length > 0 ? trimmed : fallback;
}

/**
 * Baut den CommandContext zur Feuerzeit — Muster aus HotkeyHost.tsx. Wird bei
 * jedem Filterlauf UND beim Aktivieren frisch erstellt, damit when()/run() immer
 * die aktuellen Werte sehen.
 */
function buildCtx(
  sizeClass: CommandContext['sizeClass'],
  pathname: string,
  navigate: NavigateFunction,
  t: CommandContext['t'],
): CommandContext {
  return {
    sizeClass,
    pathname,
    hasDetail: false,
    overlayDepth: getOverlayDepth(),
    navigate,
    t,
  };
}

// ---------------------------------------------------------------------------
// Komponente
// ---------------------------------------------------------------------------

export function CommandPalette(): JSX.Element {
  const { t } = useT();
  const navigate = useNavigate();
  const location = useLocation();
  const sizeClass = useSizeClass();

  const [open, setOpen] = useState(false);
  const [queryText, setQueryText] = useState('');
  const [activeKey, setActiveKey] = useState<string | null>(null);

  const dialogRef = useRef<HTMLDialogElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  // Ausloeser merken, um den Fokus beim Schliessen zurueckzugeben.
  const triggerRef = useRef<HTMLElement | null>(null);
  const overlayId = useId();
  const listboxId = useId();

  // Local-first-Bestand (Dexie/React-Query-Cache) — kein fetch.
  const characters = useCharactersLocal();
  const worldElements = useWorldElementsLocal({ exclude_game_settings: true });
  const campaigns = useCampaignsLocal();
  const tableRounds = useTableRoundsLocal();

  // Oeffnen-Kommando in der Registry (⌘K). setOpen ist stabil (useState-Setter),
  // deshalb ist das Command-Objekt ueber Renders hinweg identisch — die
  // Latest-Ref in useCommandScope liest ohnehin den frischen Stand.
  const openCommand = useMemo<Command>(() => ({
    id: 'command.palette.open',
    titleKey: 'command.open',
    keys: ['mod+k'],
    group: 'global',
    scope: 'global',
    run: () => {
      // P26-Verify: erneutes ⌘K bei offener Palette darf den gemerkten
      // Ausloeser NICHT mit dem Palette-Suchfeld ueberschreiben (die
      // Fokus-Rueckgabe liefe sonst ins Leere) — No-op.
      setOpen((prev) => {
        if (prev) return prev;
        triggerRef.current = (typeof document !== 'undefined'
          && document.activeElement instanceof HTMLElement)
          ? document.activeElement
          : null;
        return true;
      });
    },
  }), []);
  useCommandScope('global', [openCommand]);

  const requestClose = useCallback(() => {
    setOpen(false);
  }, []);

  // Natives <dialog> oeffnen/schliessen + overlayStack-An-/Abmeldung.
  useEffect(() => {
    if (!open) return undefined;
    const el = dialogRef.current;
    if (el) {
      // showModal()/close() sind in jsdom (Test) nicht implementiert — dann als
      // Fallback das open-Attribut setzen, damit das <dialog> sichtbar/abfragbar
      // bleibt. Im Browser ist showModal der echte modale Pfad (Top-Layer).
      if (typeof el.showModal === 'function') {
        if (!el.open) {
          try { el.showModal(); } catch { el.open = true; }
        }
      } else {
        el.open = true;
      }
    }

    const entry = { id: overlayId, close: requestClose };
    const unregister = pushOverlay(entry);

    // Suchfeld beim Oeffnen fokussieren (nach dem Commit).
    const focusTimer = window.setTimeout(() => {
      inputRef.current?.focus();
    }, 0);

    return () => {
      window.clearTimeout(focusTimer);
      unregister();
      // el aus dem Effekt-Setup wiederverwenden (das <dialog> ist stabil,
      // solange offen) — kein erneutes dialogRef.current im Cleanup lesen.
      if (el) {
        if (typeof el.close === 'function' && el.open) {
          try { el.close(); } catch { el.open = false; }
        } else {
          el.open = false;
        }
      }
      // Beim Schliessen zuruecksetzen + Fokus an den Ausloeser zurueck.
      setQueryText('');
      setActiveKey(null);
      const trigger = triggerRef.current;
      if (trigger && typeof document !== 'undefined' && document.contains(trigger)) {
        trigger.focus();
      }
      triggerRef.current = null;
    };
  }, [open, overlayId, requestClose]);

  // Gefilterte + gerankte Ergebnisse, gruppiert nach Sektion.
  const results = useMemo<PaletteResult[]>(() => {
    if (!open) return [];
    const query = queryText.trim();
    const ctx = buildCtx(sizeClass, location.pathname, navigate, t);
    const out: PaletteResult[] = [];

    // (a) Kommandos — nur aktive (when(ctx)); das Oeffnen-Kommando selbst nie.
    // Dedupe per cmd.id (wie ShortcutHelp): doppelt montierte Scopes erzeugen
    // sonst doppelte React-Keys.
    const seenCommandIds = new Set<string>();
    for (const cmd of getRegisteredCommands()) {
      if (cmd.id === 'command.palette.open') continue;
      if (seenCommandIds.has(cmd.id)) continue;
      seenCommandIds.add(cmd.id);
      if (cmd.when && !cmd.when(ctx)) continue;
      const label = t(cmd.titleKey);
      const score = fuzzyScore(label, query);
      if (score === null) continue;
      out.push({
        key: `command:${cmd.id}`,
        kind: 'command',
        label,
        score,
        icon: SECTION_ICON.command,
        run: () => {
          const freshCtx = buildCtx(sizeClass, location.pathname, navigate, t);
          void cmd.run(freshCtx);
        },
      });
    }

    // (b) Entitaeten aus dem lokalen Cache.
    const pushEntity = (
      kind: Exclude<ResultKind, 'command'>,
      id: number,
      label: string,
      to: string,
    ) => {
      const score = fuzzyScore(label, query);
      if (score === null) return;
      out.push({
        key: `${kind}:${id}`,
        kind,
        label,
        score,
        icon: SECTION_ICON[kind],
        run: () => navigate(to),
      });
    };

    for (const c of characters.data ?? []) {
      pushEntity('character', c.id, nameOf(c, t('command.section.characters')), `/character-chat/${c.id}`);
    }
    for (const w of worldElements.data ?? []) {
      // Alt-Vertrag H: /world-element-edit?id= bleibt dauerhaft (docs/30 §8.4).
      pushEntity('world', w.id, nameOf(w, t('command.section.world')), `/world-element-edit?id=${w.id}`);
    }
    for (const camp of campaigns.data ?? []) {
      pushEntity('campaign', camp.id, nameOf(camp, t('command.section.campaigns')), `/chat/${camp.id}`);
    }
    for (const r of tableRounds.data ?? []) {
      pushEntity('round', r.id, nameOf(r, t('command.section.rounds')), `/table-round-chat/${r.id}`);
    }

    // Innerhalb der Sektion nach Score sortieren; Sektions-Reihenfolge fest.
    out.sort((a, b) => {
      const sectionDelta = SECTION_ORDER.indexOf(a.kind) - SECTION_ORDER.indexOf(b.kind);
      if (sectionDelta !== 0) return sectionDelta;
      if (b.score !== a.score) return b.score - a.score;
      return a.label.localeCompare(b.label);
    });
    // P26-Verify: Sektions-Deckel — bei leerer Query sonst 600+ <li> beim
    // blossen Oeffnen (583 Beispiel-Kosmos-Elemente allein). Score sortiert,
    // dann je Sektion Top-N; Kommandos bleiben ungedeckelt (endliche Menge).
    const CAP_PER_SECTION = 20;
    const capped: PaletteResult[] = [];
    const perKind = new Map<string, number>();
    out.sort((a, b) => b.score - a.score);
    for (const r of out) {
      if (r.kind !== 'command') {
        const n = perKind.get(r.kind) ?? 0;
        if (n >= CAP_PER_SECTION) continue;
        perKind.set(r.kind, n + 1);
      }
      capped.push(r);
    }
    return capped;
  }, [
    open,
    queryText,
    sizeClass,
    location.pathname,
    navigate,
    t,
    characters.data,
    worldElements.data,
    campaigns.data,
    tableRounds.data,
  ]);

  // Aktive Option gueltig halten: nach jedem Filterlauf auf den ersten Treffer
  // setzen, wenn die bisherige Auswahl verschwunden ist.
  useEffect(() => {
    if (!open) return;
    if (results.length === 0) {
      setActiveKey(null);
      return;
    }
    setActiveKey((prev) => (prev && results.some((r) => r.key === prev) ? prev : results[0].key));
  }, [open, results]);

  const activate = useCallback((key: string) => {
    const hit = results.find((r) => r.key === key);
    if (!hit) return;
    // Erst schliessen (Fokus-Rueckgabe), dann die Aktion feuern.
    requestClose();
    hit.run();
  }, [results, requestClose]);

  const moveActive = useCallback((delta: number) => {
    if (results.length === 0) return;
    setActiveKey((prev) => {
      const currentIndex = prev ? results.findIndex((r) => r.key === prev) : -1;
      const nextIndex = (currentIndex + delta + results.length) % results.length;
      return results[nextIndex].key;
    });
  }, [results]);

  const handleInputKeyDown = useCallback((event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      moveActive(1);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      moveActive(-1);
    } else if (event.key === 'Enter') {
      event.preventDefault();
      if (activeKey) activate(activeKey);
    }
  }, [activate, activeKey, moveActive]);

  // Esc/Cancel des nativen <dialog>: nur schliessen, wenn diese Palette die
  // oberste Overlay-Ebene ist (cross-type-Semantik, peekTop). preventDefault,
  // damit der Browser das <dialog> nicht selbst schliesst (wir fuehren den
  // State + Fokus-Rueckgabe im Effekt-Cleanup).
  const handleCancel = useCallback((event: React.SyntheticEvent<HTMLDialogElement>) => {
    event.preventDefault();
    if (peekTop()?.id !== overlayId) return;
    requestClose();
  }, [overlayId, requestClose]);

  if (!open) {
    // Das <dialog> lebt nur, solange offen — das Oeffnen-Kommando bleibt via
    // useCommandScope aktiv registriert (haengt nicht am DOM-Knoten).
    return <></>;
  }

  // Ergebnisse in Sektionen fuers Rendering gruppieren (Reihenfolge = results).
  const sections: { kind: ResultKind; items: PaletteResult[] }[] = [];
  for (const kind of SECTION_ORDER) {
    const items = results.filter((r) => r.kind === kind);
    if (items.length > 0) sections.push({ kind, items });
  }

  return (
    <dialog
      ref={dialogRef}
      className="lorath-command-palette"
      aria-label={t('command.open')}
      onCancel={handleCancel}
      onClose={requestClose}
    >
      <div className="lorath-command-palette__field">
        <Icon name="search" size={20} />
        <input
          ref={inputRef}
          type="text"
          role="combobox"
          aria-expanded={results.length > 0}
          aria-controls={listboxId}
          aria-activedescendant={activeKey ?? undefined}
          aria-label={t('command.placeholder')}
          placeholder={t('command.placeholder')}
          value={queryText}
          onChange={(e) => setQueryText(e.target.value)}
          onKeyDown={handleInputKeyDown}
          autoComplete="off"
          spellCheck={false}
        />
      </div>

      {results.length === 0 ? (
        <p className="lorath-command-palette__empty">{t('command.empty')}</p>
      ) : (
        <ul id={listboxId} role="listbox" aria-label={t('command.open')}>
          {sections.map((section) => (
            <li key={section.kind} role="presentation">
              <div className="lorath-command-palette__section" role="presentation">
                {t(SECTION_TITLE_KEY[section.kind])}
              </div>
              <ul role="presentation">
                {section.items.map((item) => (
                  <li
                    key={item.key}
                    id={item.key}
                    role="option"
                    aria-selected={item.key === activeKey}
                    className="lorath-command-palette__option"
                    // onMouseDown statt onClick: verhindert, dass das Suchfeld
                    // vor der Aktivierung den Fokus (und damit blur-Effekte)
                    // verliert; preventDefault haelt den Fokus im Feld.
                    onMouseDown={(e) => {
                      e.preventDefault();
                      activate(item.key);
                    }}
                    onMouseEnter={() => setActiveKey(item.key)}
                  >
                    <Icon name={item.icon} size={18} />
                    <span>{item.label}</span>
                  </li>
                ))}
              </ul>
            </li>
          ))}
        </ul>
      )}
    </dialog>
  );
}

export default CommandPalette;
